import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { FaSearch, FaMapMarkerAlt } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { Briefcase, CalendarCheck, ArrowRight } from 'lucide-react';

const ROLES = [
  { text: 'Software Engineer',  count: '2,34,000+' },
  { text: 'Delivery Partner',   count: '1,80,000+' },
  { text: 'Data Scientist',     count: '68,000+'   },
  { text: 'Electrician',        count: '52,000+'   },
  { text: 'Product Manager',    count: '45,000+'   },
  { text: 'Security Guard',     count: '98,000+'   },
  { text: 'React Developer',    count: '85,000+'   },
  { text: 'Driver',             count: '2,10,000+' },
];

const CITIES = ['Bengaluru', 'Mumbai', 'Delhi NCR', 'Pune', 'Hyderabad', 'Chennai'];

function Typewriter() {
  const [display, setDisplay] = useState('');
  const [phase, setPhase]     = useState('typing');
  const [idx, setIdx]         = useState(0);
  const charRef  = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    const role = ROLES[idx].text;
    const tick = () => {
      if (phase === 'typing') {
        if (charRef.current < role.length) {
          charRef.current++;
          setDisplay(role.slice(0, charRef.current));
          timerRef.current = setTimeout(tick, 70);
        } else {
          setPhase('pause');
        }
      } else if (phase === 'pause') {
        timerRef.current = setTimeout(() => setPhase('erasing'), 1400);
      } else {
        if (charRef.current > 0) {
          charRef.current--;
          setDisplay(role.slice(0, charRef.current));
          timerRef.current = setTimeout(tick, 35);
        } else {
          charRef.current = 0;
          setIdx(i => (i + 1) % ROLES.length);
          setPhase('typing');
        }
      }
    };
    clearTimeout(timerRef.current);
    tick();
    return () => clearTimeout(timerRef.current);
  }, [phase, idx]);

  return (
    <span className="text-brand-purple">
      {display}
      <span className="inline-block w-[2px] h-[1em] bg-brand-purple ml-0.5 align-middle animate-pulse" />
    </span>
  );
}

const JOB_CATEGORIES = [
  {
    icon: Briefcase,
    label: 'Browse Jobs',
    path: '/search',
    iconColor: '#7C3AED',
    iconBg: '#F5F3FF',
    borderHover: 'hover:border-brand-purple/40',
  },
  {
    icon: CalendarCheck,
    label: 'Walk-in Drives',
    path: '/search?tab=walkin',
    iconColor: '#059669',
    iconBg: '#F0FDF4',
    borderHover: 'hover:border-green-300',
  },
];

export default function Hero({ onStartSearching }) {
  const [location, setLocation] = useState('');
  const navigate = useNavigate();

  return (
    <section className="relative min-h-screen flex flex-col items-center justify-center
                        pt-16 md:pt-[72px] px-4 sm:px-6 overflow-hidden bg-paper">

      {/* Grid background */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(to right,#e2e8f0 1px,transparent 1px),' +
            'linear-gradient(to bottom,#e2e8f0 1px,transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />

      {/* Colour blobs */}
      <div className="absolute top-1/3 -left-32 w-[600px] h-[600px] rounded-full
                      bg-brand-purple opacity-[0.06] blur-[120px] pointer-events-none" />
      <div className="absolute top-1/2 -right-32 w-[400px] h-[400px] rounded-full
                      bg-brand-saffron opacity-[0.06] blur-[100px] pointer-events-none" />

      <div className="relative z-10 w-full max-w-5xl mx-auto text-center">

        {/* Eyebrow */}
        <motion.span
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="inline-flex items-center gap-2 px-4 py-1.5 mb-8
                     text-xs font-black tracking-widest uppercase
                     bg-ink-100 text-ink-700 rounded-full"
        >
          <span className="w-2 h-2 rounded-full bg-brand-green animate-pulse" />
          India's All-in-One Work Ecosystem
        </motion.span>

        {/* Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="font-display font-black leading-[1.04] tracking-tight mb-6
                     text-4xl sm:text-5xl md:text-6xl lg:text-[72px] text-ink-900"
        >
          From Every Corner of <span className="text-brand-saffron">Bharat</span><br />
          To Every Kind of Work.
        </motion.h1>

        {/* Sub-headline */}
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-lg md:text-xl text-ink-500 max-w-3xl mx-auto mb-10 leading-relaxed"
        >
          Jobs, placements and workforce for white-collar, blue-collar,
          government and contract work. One platform. Built for India.
        </motion.p>

        {/* Search widget */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="w-full max-w-2xl mx-auto"
        >
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center
                          bg-white border border-gray-200 rounded-2xl shadow-glass-lg p-2 gap-2">

            {/* Role field (typewriter) */}
            <div className="flex-1 flex items-center gap-3 px-4 py-3">
              <FaSearch className="text-ink-300 shrink-0" />
              <span className="text-base font-medium text-ink-900 truncate">
                <Typewriter />
              </span>
            </div>

            <div className="hidden sm:block w-px bg-gray-100 self-stretch" />

            {/* Location field */}
            <div className="flex-1 flex items-center gap-3 px-4 py-3">
              <FaMapMarkerAlt className="text-ink-300 shrink-0" />
              <input
                type="text"
                placeholder="Location..."
                value={location}
                onChange={e => setLocation(e.target.value)}
                aria-label="Location"
                className="flex-1 bg-transparent text-base text-ink-900
                           placeholder-ink-300 outline-none font-medium min-w-0"
              />
            </div>

            <button
              onClick={() => navigate(location.trim() ? `/search?loc=${encodeURIComponent(location.trim())}` : '/search')}
              className="px-8 py-3 rounded-xl font-black text-sm tracking-wide text-white
                         bg-brand-purple hover:brightness-110 active:scale-95
                         transition-all shadow-purple whitespace-nowrap"
            >
              Search Jobs
            </button>
          </div>

          {/* City chips */}
          <div className="flex flex-wrap justify-center gap-2 mt-4">
            {CITIES.map(city => (
              <button
                key={city}
                onClick={() => navigate(`/search?loc=${encodeURIComponent(city)}`)}
                className="px-3 py-1 rounded-full text-xs font-semibold
                           bg-white border border-gray-200 text-ink-500
                           hover:border-brand-purple hover:text-brand-purple transition-all"
              >
                {city}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Job category tiles */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.55 }}
          className="mt-8 max-w-2xl mx-auto"
        >
          <p className="text-[11px] font-black uppercase tracking-widest text-ink-300 mb-3">
            Browse by category
          </p>
          <div className="grid grid-cols-2 gap-3 max-w-sm mx-auto">
            {JOB_CATEGORIES.map(cat => {
              const Icon = cat.icon;
              return (
                <button
                  key={cat.label}
                  onClick={() => navigate(cat.path)}
                  className={`group flex items-center gap-3 px-4 py-3.5 rounded-2xl border border-gray-100 bg-white text-left transition-all shadow-[0_1px_3px_rgba(0,0,0,0.06)] hover:shadow-glass ${cat.borderHover}`}
                >
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                    style={{ background: cat.iconBg, color: cat.iconColor }}
                  >
                    <Icon className="w-4 h-4" strokeWidth={2} />
                  </div>
                  <span className="font-bold text-[13px] text-ink-900 leading-snug">{cat.label}</span>
                  <ArrowRight className="w-3.5 h-3.5 text-ink-300 group-hover:text-ink-700 group-hover:translate-x-0.5 transition-all ml-auto shrink-0" />
                </button>
              );
            })}
          </div>
        </motion.div>

        {/* Hiring nudge */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="mt-6 text-sm text-ink-300"
        >
          Are you hiring?{' '}
          <a
            href="https://recruiter.jobxbharat.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="font-bold text-brand-saffron hover:underline"
          >
            Post a Job for Free →
          </a>
        </motion.p>

      </div>
    </section>
  );
}
